<template>
  <div class="hot-words-page page p-6">
    <!-- 热词设置区域 -->
    <div class="section-title text-white text-xl mb-4">{{ $t('voiceModels.hotWordSettings') }}</div>
    <div class="main_bg rounded-2xl p-8 mb-8">
      <!-- 结束对话 -->
      <div class="flex items-center gap-x-6 mb-6">
        <div class="w-32 text-white text-lg">{{ $t('voiceModels.endConversation') }}</div>
        <div class="flex-1 relative">
          <el-input
            v-model="endConversationWord"
            :placeholder="$t('voiceModels.interruptPlaceholder')"
            maxlength="4"
            show-word-limit
            class="custom-input"
            @keyup.enter.native="handleAddEndConversation"
          ></el-input>
        </div>
        <el-button 
          type="primary" 
          class="add-btn"
          :loading="addingEndConversation"
          @click="handleAddEndConversation"
        >
          {{ $t('voiceModels.add') }}
        </el-button>
      </div>

      <!-- 特殊短语语音识别 -->
      <div class="flex items-center gap-x-6">
        <div class="w-32 text-white text-lg leading-tight">{{ $t('voiceModels.specialPhrase') }}</div>
        <div class="flex-1 relative">
          <el-input
            v-model="specialPhraseWord"
            :placeholder="$t('voiceModels.specialPhraseDefault')"
            maxlength="4"
            show-word-limit
            class="custom-input"
            @keyup.enter.native="handleAddSpecialPhrase"
          ></el-input>
        </div>
        <el-button 
          type="primary" 
          class="add-btn"
          :loading="addingSpecialPhrase"
          @click="handleAddSpecialPhrase"
        >
          {{ $t('voiceModels.add') }}
        </el-button>
      </div>
    </div>

    <!-- 热词库区域 -->
    <div class="section-title text-white text-xl mb-4">{{ $t('voiceModels.hotWordLibrary') }}</div>
    <div class="main_bg rounded-2xl p-6">
      <div class="table-container rounded-xl overflow-hidden">
        <el-table
          :data="tableData"
          style="width: 100%"
          height="500"
          class="custom-table"
          :header-cell-style="headerStyle"
          :cell-style="cellStyle"
          v-loading="loadingHotWords"
        >
          <el-table-column
            prop="seq"
            :label="$t('voiceModels.sequence')"
            width="120"
            align="center"
          >
            <template slot-scope="scope">
              {{ String(scope.$index + 1).padStart(2, '0') }}
            </template>
          </el-table-column>
          <el-table-column
            prop="text"
            :label="$t('voiceModels.hotWord')"
            align="center"
          ></el-table-column>
          <el-table-column
            :label="$t('voiceModels.operation')"
            width="120"
            align="center"
          >
            <template slot-scope="scope">
              <span class="delete-link cursor-pointer" @click="handleDelete(scope.row)">{{ $t('voiceModels.delete') }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import vocabularyApi from '@/network/vocabulary/index'

export default {
  name: 'HotWords',
  data() {
    return {
      endConversationWord: '',
      specialPhraseWord: '',
      tableData: [],
      loadingHotWords: false,
      addingEndConversation: false,
      addingSpecialPhrase: false,
      headerStyle: {
        backgroundColor: '#404761',
        color: '#FFFFFF',
        fontWeight: 'normal',
        borderBottom: 'none',
        height: '60px'
      },
      cellStyle: {
        backgroundColor: 'transparent',
        color: '#FFFFFF',
        borderBottom: '1px solid #4A4A6A',
        height: '60px'
      }
    }
  },
  mounted() {
    this.loadHotWords()
  },
  methods: {
    // 加载热词列表
    async loadHotWords() {
      this.loadingHotWords = true
      try {
        const res = await vocabularyApi.getUserHotWords()
        if (res.statusCode === 100200) {
          // 根据接口返回的数据结构处理
          // 如果返回的是数组，直接使用；如果是对象，可能需要处理
          if (Array.isArray(res.data)) {
            this.tableData = res.data
          } else if (res.data && Array.isArray(res.data.list)) {
            this.tableData = res.data.list
          } else {
            this.tableData = []
          }
        } else {
          this.$message.error(res.message || '获取热词列表失败')
        }
      } catch (error) {
        const errorMsg = error.message || error.response?.data?.message || '获取热词列表失败'
        this.$message.error(errorMsg)
        this.tableData = []
      } finally {
        this.loadingHotWords = false
      }
    },
    // 添加结束对话热词
    async handleAddEndConversation() {
      const hotWord = this.endConversationWord.trim()
      if (!hotWord) {
        this.$message.warning('请输入结束对话热词')
        return
      }

      this.addingEndConversation = true
      try {
        const res = await vocabularyApi.addHotWord({
          hotWord: hotWord
        })
        
        if (res.statusCode === 100200) {
          this.$message.success('添加成功')
          this.endConversationWord = ''
          // 重新加载热词列表
          await this.loadHotWords()
        } else {
          this.$message.error(res.message || '添加失败')
        }
      } catch (error) {
        const errorMsg = error.message || error.response?.data?.message || '添加失败'
        this.$message.error(errorMsg)
      } finally {
        this.addingEndConversation = false
      }
    },
    // 添加特殊短语热词
    async handleAddSpecialPhrase() {
      const hotWord = this.specialPhraseWord.trim()
      if (!hotWord) {
        this.$message.warning('请输入特殊短语热词')
        return
      }

      this.addingSpecialPhrase = true
      try {
        const res = await vocabularyApi.addHotWord({
          hotWord: hotWord
        })
        
        if (res.statusCode === 100200) {
          this.$message.success('添加成功')
          this.specialPhraseWord = ''
          // 重新加载热词列表
          await this.loadHotWords()
        } else {
          this.$message.error(res.message || '添加失败')
        }
      } catch (error) {
        const errorMsg = error.message || error.response?.data?.message || '添加失败'
        this.$message.error(errorMsg)
      } finally {
        this.addingSpecialPhrase = false
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除该热词吗？', '提示', {
          type: 'warning'
        })
        
        const res = await vocabularyApi.removeHotWord({
          text: row.text
        })
        
        if (res.statusCode === 100200) {
          this.$message.success('删除成功')
          // 重新加载热词列表
          await this.loadHotWords()
        } else {
          this.$message.error(res.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          const errorMsg = error.message || error.response?.data?.message || '删除失败'
          this.$message.error(errorMsg)
        }
      }
    }
  }
}
</script>

<style scoped>
.page {
  background-color: #232A3E;
  min-height: 100%;
}

.main_bg {
  background-color: #333A53;
}

::v-deep .custom-input .el-input__inner {
  background-color: #2D334B !important;
  border: 1px solid #4A4A6A !important;
  color: #FFFFFF !important;
  border-radius: 12px;
  height: 54px;
  padding-right: 50px;
}

::v-deep .custom-input .el-input__count,
::v-deep .custom-input .el-input__count .el-input__count-inner {
  background: transparent !important;
  background-color: transparent !important;
  bottom: 0;
  height: 54px;
  display: flex;
  align-items: center;
  color: #6C7293 !important;
}

.add-btn {
  background: linear-gradient(135deg, #8E54E9, #4776E6);
  border: none;
  border-radius: 12px;
  width: 120px;
  height: 50px;
  font-size: 16px;
  box-shadow: 0 4px 15px rgba(113, 60, 244, 0.3);
}

.add-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.table-container {
  border: 1px solid #4A4A6A;
}

::v-deep .custom-table {
  background-color: transparent !important;
}

::v-deep .el-table {
  background-color: transparent !important;
}

::v-deep .el-table tr {
  background-color: transparent !important;
}

::v-deep .el-table::before {
  display: none;
}

.delete-link {
  color: #FF5E5E;
}

.delete-link:hover {
  color: #FF8080;
  text-decoration: underline;
}

/* 隐藏表格hover时的背景色，或者设置成自定义颜色 */
::v-deep .el-table--enable-row-hover .el-table__row:hover > td {
  background-color: rgba(255, 255, 255, 0.05) !important;
}
</style>
